import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  UpdateAssociationLocationInput,
  UpdateAssociationLocationMutation,
  UpdateAssociationLocationMutationVariables,
} from "@/graphql/codegen/graphql"
import { UpdateAssociationLocationDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseUpdateAssociationLocationReturn {
  updateLocation: (input: UpdateAssociationLocationInput) => Promise<boolean>
  isUpdating: boolean
}

export const useUpdateAssociationLocation = (): UseUpdateAssociationLocationReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, executeMutation] = useMutation<
    UpdateAssociationLocationMutation,
    UpdateAssociationLocationMutationVariables
  >(UpdateAssociationLocationDocument)

  const updateLocation = useCallback(
    async (input: UpdateAssociationLocationInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.companyProductAssociation.updateLocation

      const hasError = error || output?.__typename === "UpdateAssociationLocationFailure"

      if (hasError) {
        const message =
          output?.__typename === "UpdateAssociationLocationFailure" ? output.error.message : error?.message
        notify({
          type: NotificationType.Error,
          silent: false,
          message: message ?? "Failed to update location. Please try again.",
        })
        return false
      }

      notify({
        type: NotificationType.Success,
        silent: false,
        message: "Location updated",
      })
      return true
    },
    [executeMutation, notify]
  )

  return { updateLocation, isUpdating: fetching }
}
